'use client'

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { MapPin, FileText } from 'lucide-react';
import { HiChevronUp } from "react-icons/hi2"

interface FooterProps {
  hideContent?: boolean;
}

const Footer: React.FC<FooterProps> = ({ hideContent = false }) => {

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const RECRUITMENT_TYPE_ID = process.env.NEXT_PUBLIC_RECRUITMENT_ID;
  const PRIVACY_POLICY_ID = process.env.NEXT_PUBLIC_PRIVACY_POLICY_ID;
  const TERMS_OF_USE_ID = process.env.NEXT_PUBLIC_TERMS_OF_USE_ID;

  return (
    <footer className="bg-[#EFEFEF] mt-10 rounded-t-3xl relative">
      <button
        onClick={scrollToTop}
        className="absolute top-0 right-8 z-50 w-9 h-9 bg-white rounded-full  border border-green-600 flex items-center justify-center hover:bg-green-50 transition-all duration-300 -translate-y-1/2"
        aria-label="Quay lại đầu trang"
      >
        <HiChevronUp className="h-3 w-4 text-green-600" strokeWidth={2} />
      </button>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 lg:py-12">
        {!hideContent && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-0 md:gap-10 divide-y md:divide-y-0 md:divide-x divide-gray-300/50">

            {/* Địa chỉ */}
            <div className="pb-4 md:pb-0 md:pr-8 lg:pr-10 text-center md:text-left">
              <Image
                src="/images/logo Rovi Holdings.png"
                alt="RVH"
                width={175}
                height={47}
                className="h-12 w-auto mx-auto md:mx-0 mb-5"
              />
              <div className="space-y-3 text-xs text-gray-700">
                <div className="flex items-start gap-3 justify-center md:justify-start">
                  <MapPin className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <div className="font-semibold text-xs inline mr-2">Trụ sở:</div>
                    <span className="text-xs">Số 4 Hát Giang, Phường Tân Sơn Hòa, TP Hồ Chí Minh, Việt Nam</span>
                  </div>
                </div>

                <div className="flex items-start gap-3 justify-center md:justify-start">
                  <FileText className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-xs inline mr-2">MST:</span>
                    <span className="text-xs">0317231535</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Liên kết nhanh */}
            <div className="py-4 md:py-0 md:px-8 text-center md:text-left">
              <div className="text-xs pt-2 md:pt-5">
                <div className="text-xs md:text-sm font-semibold text-[#000000] mb-5">
                  LIÊN KẾT NHANH
                </div>
                <div className="grid grid-cols-2 gap-x-10 gap-y-2 max-w-md mx-auto md:mx-0">
                  <div className="space-y-4">
                    <Link href="/about" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Về chúng tôi
                    </Link>
                    <Link href="/news" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Tin tức
                    </Link>
                    <Link href="news?tuyen-dung" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Tuyển dụng
                    </Link>
                    <Link href="/contact" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Liên hệ
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {/* Đã cập nhật link sang dạng slug /ecosystem/ten-cong-ty */}
                    <Link href="/ecosystem/rovi-travel" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Rovi Travel
                    </Link>
                    <Link href="/ecosystem/rovi-logistics" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Rovi Logistics
                    </Link>
                    <Link href="/ecosystem/rovi-hospitality" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Rovi Hospitality
                    </Link>
                    <Link href="/ecosystem/rovi-investment" rel="noopener noreferrer" className="block text-xs text-[#000000] hover:text-green-700 transition-colors">
                      Rovi Invesment
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Liên hệ */}
            <div className="pt-4 md:pt-0 md:pl-8 text-center md:text-left">
              <div className="space-y-4">
                <div className="text-base md:text-lg font-medium text-[#000000] underline decoration-[#006633] decoration-0 underline-offset-7">
                  Hotline: 19002681
                </div>
                <div className="text-base md:text-lg font-medium text-[#000000]  underline decoration-[#006633] decoration-0 underline-offset-7">
                  Email: info@roviholdings.vn
                </div>

                <div className="flex items-center justify-center md:justify-start gap-3 pt-3">
                  <a href="https://www.facebook.com/roviholdings" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                      <path d="M22 12.073C22 6.477 17.523 2 11.927 2S1.854 6.477 1.854 12.073c0 5.022 3.657 9.193 8.438 9.927v-7.031H7.898v-2.896h2.394V9.845c0-2.368 1.407-3.676 3.561-3.676 1.031 0 2.109.184 2.109.184v2.32h-1.188c-1.173 0-1.538.728-1.538 1.475v1.774h2.617l-.418 2.896h-2.199v7.031c4.78-.734 8.437-4.905 8.437-9.927z" />
                    </svg>
                  </a>
                  <a href="https://youtube.com/@roviholdings308" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="h-8 w-8 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors">
                    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 4-8 4z" fill="#ffffff" />
                    </svg>
                  </a>
                  <a href="https://zalo.me/827254431056665603" target="_blank" rel="noopener noreferrer" aria-label="Zalo" className="h-8 w-8 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold hover:bg-sky-600 transition-colors">
                    Zalo
                  </a>
                  <a href="https://linkedin.com/company/roviholdings/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="h-8 w-8 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition-colors">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                      <path d="M20.447 20.452h-3.554V14.84c0-1.338-.027-3.063-1.868-3.063-1.868 0-2.154 1.46-2.154 2.968v5.707H9.317V9h3.414v1.561h.049c.476-.9 1.637-1.848 3.37-1.848 3.605 0 4.27 2.372 4.27 5.455v6.284z" />
                      <path d="M5.337 7.433a2.062 2.062 0 11.001-4.125 2.062 2.062 0 0 1-.001 4.125z" />
                      <path d="M6.814 20.452H3.861V9h2.953v11.452z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Copyright */}
        <div className="mt-6 pt-4 text-gray-600">
          <div className="border-t border-gray-300/50" />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-3">
            <div className="text-[10px] text-[#8A8A8A]">
              © 2025 <span className="font-semibold text-[#000000]">ROVI HOLDINGS</span>. All Rights Reserved.
            </div>
            <div className="flex items-center gap-4 text-[10px] text-[#666666]">
              <Link href={`/news/${PRIVACY_POLICY_ID}`} target="_blank" rel="noopener noreferrer" className="hover:text-green-700 transition-colors">
                Chính sách bảo mật
              </Link>
              <Link href={`/news/${TERMS_OF_USE_ID}`} target="_blank" rel="noopener noreferrer" className="hover:text-green-700 transition-colors">
                Điều khoản sử dụng
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;